/**
 * Neyra — local mock storage layer.
 * All data lives in localStorage. No backend, no network.
 */

const KEYS = {
    USERS: "neara.users",
    SESSION: "neara.session",       // current userId
    SIGNUP_DRAFT: "neara.signupDraft",
    OTP: "neara.otp",               // { code, purpose, identifier }
    THEME: "neara-theme",           // (owned by ThemeContext)
};

const read = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch {
        return fallback;
    }
};

const write = (key, value) => {
    localStorage.setItem(key, JSON.stringify(value));
};

const remove = (key) => localStorage.removeItem(key);

/* ---------- Users ---------- */

export const getUsers = () => {
    const list = read(KEYS.USERS, []);
    return Array.isArray(list) ? list.filter(Boolean) : [];
};
export const saveUsers = (list) => write(KEYS.USERS, list);

export const findUser = (predicate) => getUsers().find(predicate);

export const findUserByUsername = (username) => {
    if (!username) return null;
    const lower = String(username).toLowerCase();
    return getUsers().find((u) => u && (u.username || "").toLowerCase() === lower) || null;
};

export const findUserByIdentifier = (identifier) => {
    const id = String(identifier || "").trim().toLowerCase();
    if (!id) return null;
    return (
        getUsers().find(
            (u) =>
                u.username?.toLowerCase() === id ||
                u.email?.toLowerCase() === id ||
                u.phone?.toLowerCase() === id
        ) || null
    );
};

export const upsertUser = (user) => {
    const list = getUsers();
    const idx = list.findIndex((u) => u.id === user.id);
    if (idx >= 0) list[idx] = user;
    else list.push(user);
    saveUsers(list);
    return user;
};

/* ---------- Session ---------- */

export const getSessionUser = () => {
    const id = read(KEYS.SESSION, null);
    if (!id) return null;
    return getUsers().find((u) => u.id === id) || null;
};
export const setSessionUser = (userId) => write(KEYS.SESSION, userId);
export const clearSession = () => remove(KEYS.SESSION);

/* ---------- Signup draft (in-progress signup) ---------- */

export const getSignupDraft = () => read(KEYS.SIGNUP_DRAFT, null);
export const setSignupDraft = (draft) => write(KEYS.SIGNUP_DRAFT, draft);
export const updateSignupDraft = (patch) => {
    const cur = getSignupDraft() || {};
    const next = { ...cur, ...patch };
    setSignupDraft(next);
    return next;
};
export const clearSignupDraft = () => remove(KEYS.SIGNUP_DRAFT);

/* ---------- OTP session (pinId + identifier carrier) ---------- */

export const setOTP = (payload) => write(KEYS.OTP, payload);
export const getOTP = () => read(KEYS.OTP, null);
export const clearOTP = () => remove(KEYS.OTP);
